import React, { forwardRef, useEffect, useRef } from 'react';
import { BsSearch } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Icon, Input, InputGroup, InputLeftElement, useMergeRefs } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { inputFocusStyle } from '../../../styles/theme/components/input';
import colors from '../../../styles/theme/foundation/colors';
import SearchBlueprintType from '../../../types/SearchBlueprintType';

const SearchGlobalSearchInput = forwardRef(({ searchBlueprint,
  size = 'md',
  variant = 'primary',
  dataAnalyticsId = undefined,
  placeholder = 'Search',
  cypressId = undefined,
  ...styles }, ref) => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const mergedRef = useMergeRefs(inputRef, ref);
  const globalSearchValue = useSelector(searchBlueprint.selectGlobalSearchValue);

  const onSearchChange = ({ target: { value } }) => {
    dispatch(searchBlueprint.updateGlobalSearchFilter(value));
  };

  useEffect(() => {
    if (inputRef.current && (globalSearchValue === '' || globalSearchValue === null)) {
      inputRef.current.value = '';
    }
  }, [globalSearchValue]);

  return (
    <InputGroup
      alignItems="center"
      as={Flex}
      flex={0}
      justifyContent="flex-end"
      {...styles}
    >
      <InputLeftElement
        children={(
          <Icon
            as={BsSearch}
            boxSize="24px"
            color={colors.greyScale['600']}
          />
        )}
        height={size === 'sm' ? '32px' : '40px'}
      />
      <Input
        _focus={{ ...inputFocusStyle, width: styles.width || '300px' }}
        data-analytics-id={dataAnalyticsId}
        data-cy={cypressId}
        defaultValue={globalSearchValue}
        dusk={dataAnalyticsId}
        placeholder={placeholder}
        ref={mergedRef}
        size={size}
        textOverflow="ellipsis"
        variant={variant}
        w={styles.width || '150px'}
        onChange={onSearchChange}
        onKeyDown={evt => {
          if (evt.keyCode === 13) evt.preventDefault();
        }}
      />
    </InputGroup>
  );
});

SearchGlobalSearchInput.displayName = 'SearchGlobalSearchInput';

SearchGlobalSearchInput.propTypes = {
  /** Height of input - `sm` is `32px`, `md` is `40px`, `lg` is '48px' */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Input variant - `primary` has grey background, `secondary` has white background */
  variant: PropTypes.oneOf(['primary', 'secondary']),
  /** Data attribute on the input for analytics tracking */
  dataAnalyticsId: PropTypes.string,
  /** Cypress Testing Id */
  cypressId: PropTypes.string,
  /** Placeholder test */
  placeholder: PropTypes.string,
  searchBlueprint: SearchBlueprintType
};

export default SearchGlobalSearchInput;
